import Markdown from 'react-markdown';
import remarkBreaks from 'remark-breaks';

type Platform = 'reddit' | 'instagram' | 'twitter' | 'tiktok' | 'generic';

type MarkdownContentProps = {
	children: string;
	/** Compact mode: strips <p> tags and hides images. Default true (for card lists) */
	compact?: boolean;
	/** Platform context for auto-linking @mentions correctly */
	platform?: Platform;
	/** URL of the link (used to detect platform if not specified) */
	url?: string;
};

/**
 * Detect platform from URL
 */
function detectPlatform(url?: string): Platform {
	if (!url) return 'generic';
	if (url.includes('instagram.com')) return 'instagram';
	if (url.includes('reddit.com')) return 'reddit';
	if (url.includes('twitter.com') || url.includes('x.com')) return 'twitter';
	if (url.includes('tiktok.com')) return 'tiktok';
	return 'generic';
}

/**
 * Get the user profile URL for a platform
 */
function getUserProfileUrl(username: string, platform: Platform): string {
	switch (platform) {
		case 'instagram':
			return `https://www.instagram.com/${username}`;
		case 'twitter':
			return `https://twitter.com/${username}`;
		case 'tiktok':
			return `https://www.tiktok.com/@${username}`;
		case 'reddit':
		default:
			return `https://www.reddit.com/u/${username}`;
	}
}

/**
 * Format username display based on platform
 * Default (generic) uses Reddit format for backward compatibility
 */
function formatUsername(username: string, platform: Platform): string {
	switch (platform) {
		case 'instagram':
		case 'twitter':
		case 'tiktok':
			return `@${username}`;
		case 'reddit':
		case 'generic':
		default:
			return `u/${username}`;
	}
}

/**
 * Auto-link Reddit patterns like r/subreddit, u/username, and @username.
 * Also auto-links bare URLs and #hashtags to internal tag filter.
 *
 * Patterns handled:
 * - r/subreddit -> [r/subreddit](https://www.reddit.com/r/subreddit)
 * - u/username -> [u/username](https://www.reddit.com/u/username)
 * - @username -> Platform-appropriate link (Instagram, Twitter, etc.)
 * - #hashtag -> Internal tag filter link
 * - Bare URLs: https://example.com -> [https://example.com](https://example.com)
 * - Already linked: [text](https://...) -> unchanged
 */
function autoLinkContent(text: string, platform: Platform): string {
	// Helper to protect markdown links with placeholders
	const placeholders: string[] = [];
	function protectLinks(t: string): string {
		return t.replace(/\[([^\]]*)\]\(([^)]+)\)/g, (match) => {
			placeholders.push(match);
			return `__MDLINK_${placeholders.length - 1}__`;
		});
	}

	// Step 1: Protect existing markdown links
	let result = protectLinks(text);

	// Step 2: Auto-link bare URLs first
	result = result.replace(/(https?:\/\/[^\s<>\])"'`,]+[^\s<>\])"'`,.!?;:])/g, '[$&]($&)');

	// Step 3: Re-protect all markdown links (including newly created URL links)
	result = protectLinks(result);

	// Step 4: Auto-link Reddit patterns (now safe from URL interference)
	// Subreddits: r/subreddit
	result = result.replace(
		/(?<![/\w])r\/([A-Za-z0-9_]{2,21})(?![/\w])/g,
		'[r/$1](https://www.reddit.com/r/$1)'
	);

	// Users: u/username
	result = result.replace(
		/(?<![/\w])u\/([A-Za-z0-9_-]{3,20})(?![/\w])/g,
		'[u/$1](https://www.reddit.com/u/$1)'
	);

	// @username -> Platform-appropriate user link
	result = result.replace(/(?<![/\w])@([A-Za-z0-9_.-]{1,30})(?![/\w])/g, (_, username) => {
		const displayName = formatUsername(username, platform);
		const profileUrl = getUserProfileUrl(username, platform);
		return `[${displayName}](${profileUrl})`;
	});

	// #hashtag -> Internal tag filter (not external platform link)
	result = result.replace(/(?<![/\w])#([A-Za-z0-9_]+)(?![/\w])/g, '[#$1](?tag=hashtag:$1)');

	// Step 5: Restore all placeholders
	return result.replace(/__MDLINK_(\d+)__/g, (_, index) => placeholders[parseInt(index, 10)]);
}

export function MarkdownContent({ children, compact = true, platform, url }: MarkdownContentProps) {
	const detectedPlatform = platform ?? detectPlatform(url);
	const processedContent = autoLinkContent(children, detectedPlatform);

	return (
		<Markdown
			// In full mode, convert single \n to <br> (like GitHub Flavored Markdown)
			remarkPlugins={compact ? [] : [remarkBreaks]}
			components={{
				// External links open in new tab, internal tag links stay in app
				a: ({ href, children }) => {
					const isInternalTagLink = href?.startsWith('?tag=');
					return (
						<a
							href={href}
							target={isInternalTagLink ? undefined : '_blank'}
							rel={isInternalTagLink ? undefined : 'noopener noreferrer'}
							className="text-primary underline hover:text-primary/80"
							onClick={(e) => e.stopPropagation()}
						>
							{children}
						</a>
					);
				},
				// Compact: strip p tags. Full: preserve paragraph breaks with spacing
				p: compact
					? ({ children }) => <>{children}</>
					: ({ children }) => <p className="mb-2 last:mb-0">{children}</p>,
				// Compact: hide images (often 403). Full: show images
				img: compact
					? () => null
					: ({ src, alt }) => (
							<img src={src} alt={alt || ''} className="max-w-full h-auto rounded my-2" />
						),
			}}
		>
			{processedContent}
		</Markdown>
	);
}
